import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  fetchDocRecord,
  fetchTestRuns,
  removeTestRun,
  runTestAgain,
  uploadTestExtraction,
  type DocRecordView,
  type TestRunView,
} from '../lib/api';
import { InfoTip } from '../lib/info-tip';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { DocumentCard, type CardAction, type CardTone } from '../lib/document-card';
import { formatShortDate } from '../lib/format-date';
import { documentTypeLabel, documentTypeOrder, failureClassHeading, labels, reasonClassLabel } from '../lib/labels';
import { PagedCardList } from '../lib/paged-card-list';
import { RoleNote, useGate } from '../lib/session';
import { ResultJsonActions } from '../lib/result-json-actions';
import { AuditTrail, GateReportBlock } from './doc-parts';

const extensionOf = (name: string) => /\.(pdf|xml)$/i.exec(name)?.[1].toLowerCase();

/** The form checks, in the order the operator fixes them. `null` means the upload may go ahead. */
export function checkUpload(type: string, files: readonly { name: string }[]): string | null {
  const errors = labels.testExtraction.errors;
  if (!type) return errors.noType;
  if (files.length === 0) return errors.noFile;
  if (files.length > 2) return errors.tooMany;
  const kinds = files.map((file) => extensionOf(file.name));
  if (kinds.some((kind) => !kind)) return errors.wrongKind;
  const pdfs = kinds.filter((kind) => kind === 'pdf').length;
  if (pdfs === 0) return errors.xmlOnly;
  if (pdfs > 1 || kinds.length - pdfs > 1) return errors.twoOfAKind;
  return null;
}

/**
 * `#test-extraction` (QA-34): upload one PDF, or a PDF with its XML, into the inbox of one type,
 * then follow it. A register page (`design.md` Index-First): a short form, then the list of runs.
 * A test run is a normal document, so there is no sandbox state here; the page only reads the
 * state the pipeline already writes.
 */
export function TestExtraction() {
  const runsHeadingRef = useRef<HTMLHeadingElement>(null);
  const [type, setType] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [inputKey, setInputKey] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A new upload sends the list back to its first page, where the new run is.
  const [version, setVersion] = useState(0);
  const action = useActionError();
  const gate = useGate('administer');
  const refresh = () => setVersion((value) => value + 1);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setUploaded(null);
    const problem = checkUpload(type, files);
    setFormError(problem);
    if (problem) return;
    const answers = await ask({
      ...labels.testExtraction.confirm,
      confirmLabel: labels.testExtraction.upload,
      tone: 'primary',
    });
    if (!answers) return;
    setBusy(true);
    let docId = '';
    const ok = await action.run(async () => {
      docId = (await uploadTestExtraction(type, files)).docId;
    });
    setBusy(false);
    if (!ok) return;
    setUploaded(docId);
    setFiles([]);
    setInputKey((key) => key + 1);
    refresh();
  }

  return (
    <div className="test-extraction">
      <div className="page-head">
        <h2>{labels.testExtraction.heading}</h2>
        <p className="page-head__sub">{labels.testExtraction.sub}</p>
      </div>
      <p className="notice notice--warn">
        {labels.testExtraction.normalDocument}
        <InfoTip label={labels.testExtraction.heading} text={labels.testExtraction.normalDocumentTip} />
      </p>

      <form className="panel panel--one-line flow-md" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <p className="field field--wide">
          <label className="field__label" htmlFor="test-extraction-type">
            {labels.testExtraction.typeLabel}
          </label>
          <span className="field__control">
            <select
              id="test-extraction-type"
              required
              value={type}
              disabled={!gate.allowed}
              onChange={(event) => setType(event.target.value)}
            >
              <option value="">{labels.testExtraction.typePlaceholder}</option>
              {documentTypeOrder.map((one) => (
                <option key={one} value={one}>
                  {documentTypeLabel(one)}
                </option>
              ))}
            </select>
          </span>
        </p>
        <p className="field field--wide">
          <label className="field__label" htmlFor="test-extraction-files">
            {labels.testExtraction.filesLabel}
          </label>
          <span className="field__control file-pick">
            {/* A visually hidden input stays in the tab order and the accessibility tree; the
                `.btn` label is what the operator sees and clicks (32 px, like every control). */}
            <input
              key={inputKey}
              id="test-extraction-files"
              className="visually-hidden"
              type="file"
              accept=".pdf,.xml"
              multiple
              disabled={!gate.allowed}
              onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
            />
            <label className="btn" htmlFor="test-extraction-files" aria-disabled={!gate.allowed || undefined}>
              {labels.testExtraction.chooseFiles}
            </label>
            <span className="file-pick__names">
              {files.length > 0 ? files.map((file) => file.name).join(', ') : labels.testExtraction.noFileChosen}
            </span>
          </span>
        </p>
        {formError && (
          <p className="field__error" role="alert">
            {formError}
          </p>
        )}
        {action.notice}
        <div className="action-row flow-md">
          <RoleNote action="administer" />
          <button type="submit" className="btn btn--primary" {...gate.props} disabled={!gate.allowed || busy}>
            {busy ? labels.testExtraction.uploading : labels.testExtraction.upload}
          </button>
          {uploaded && (
            <span className="action-row__note" role="status">
              {labels.testExtraction.uploaded(uploaded)}
            </span>
          )}
        </div>
      </form>

      <section className="panel test-extraction__runs flow-md" aria-labelledby="test-runs-heading">
        <div className="panel__head">
          <h3 id="test-runs-heading" ref={runsHeadingRef} tabIndex={-1}>
            {labels.testExtraction.runsHeading}
          </h3>
        </div>
        <RoleNote action="administer" />
        <PagedCardList
          focusTarget={runsHeadingRef}
          load={(cursor, signal) =>
            fetchTestRuns(cursor, signal).then(({ runs, nextCursor }) => ({ items: runs, nextCursor }))
          }
          version={version}
          poll
          texts={{
            loading: labels.testExtraction.loading,
            empty: labels.testExtraction.runsEmpty,
            failed: labels.testExtraction.loadFailed,
          }}
          renderCard={(run, reload) => (
            <RunCard
              run={run}
              onUploaded={(docId) => {
                setUploaded(docId);
                refresh();
              }}
              onRemoved={reload}
              action={action}
            />
          )}
        />
      </section>
    </div>
  );
}

function toneOf(run: TestRunView): CardTone {
  if (run.state === 'not-processed') return 'error';
  if (run.state === 'completed' && run.verdict === 'escalate') return 'warn';
  // QA-93: a run still waiting or in progress has no outcome yet, so its pill is neutral, not green.
  if (run.state === 'waiting' || run.state === 'in-progress' || run.state === 'ignored') return 'info';
  return 'ok';
}

/** The sentence under the run id: what state it is in, and why when the state has a reason. */
export function runDetail(run: TestRunView): string {
  const text = labels.testExtraction;
  switch (run.state) {
    case 'waiting':
      return run.paused ? text.waitingPaused : '';
    case 'in-progress':
      return text.inProgressNote;
    case 'completed':
      return run.verdict === 'escalate'
        ? text.escalated(run.reason ? reasonClassLabel(run.reason) : undefined)
        : text.accepted;
    case 'ignored':
      return text.ignoredNote;
    case 'not-processed':
      return [run.reason, run.failureClass === 'misfiled' && run.detectedType ? text.detectedAs(run.detectedType) : '']
        .filter(Boolean)
        .join(' ');
  }
}

function RunCard({
  run,
  onUploaded,
  onRemoved,
  action,
}: {
  run: TestRunView;
  onUploaded: (docId: string) => void;
  onRemoved: () => void;
  action: ReturnType<typeof useActionError>;
}) {
  const text = labels.testExtraction;
  const oldType = run.docId.slice(0, run.docId.indexOf('/'));
  const [open, setOpen] = useState(false);
  const gate = useGate('administer');
  const disabledNote = gate.allowed ? undefined : labels.roleDenied;

  async function handleRunAgain() {
    const answers = await ask({
      ...text.runAgainConfirm(run.docId),
      fields: [
        {
          name: 'type',
          label: text.runAgainTypeLabel,
          kind: 'select',
          required: true,
          value: run.detectedType ?? oldType,
          options: documentTypeOrder.map((one) => ({ value: one, label: documentTypeLabel(one) })),
        },
      ],
      confirmLabel: text.runAgain,
      tone: 'primary',
    });
    if (!answers) return;
    let docId = '';
    const ok = await action.run(async () => {
      docId = (await runTestAgain(run.docId, answers.type)).docId;
    });
    if (ok) onUploaded(docId);
  }

  async function handleRemove() {
    const answers = await ask({ ...text.removeConfirm(run.docId), confirmLabel: text.removeRun, tone: 'danger' });
    if (!answers) return;
    if (await action.run(() => removeTestRun(run.docId))) onRemoved();
  }

  const stateText =
    run.state === 'not-processed' && run.failureClass ? failureClassHeading(run.failureClass) : text.states[run.state];
  // **Show the result** sits on the row's action line, first, as it is the one read of the three.
  const actions: CardAction[] = [
    ...(run.state === 'completed'
      ? [{ label: open ? text.hideResult : text.showResult, onClick: () => setOpen((prev) => !prev), expanded: open }]
      : []),
    { label: text.runAgain, onClick: () => void handleRunAgain(), disabledNote },
    {
      label: text.removeRun,
      onClick: () => void handleRemove(),
      variant: 'danger',
      icon: 'trash',
      tooltip: text.removeTooltip,
      disabledNote,
    },
  ];
  return (
    <DocumentCard
      tone={toneOf(run)}
      docId={run.docId}
      status={stateText}
      meta={<span className="mono">{formatShortDate(run.at)}</span>}
      details={[
        runDetail(run),
        <span key="files">
          <span className="doc-card__mono">{run.files.join(', ')}</span> · {text.uploadedBy(run.by)}
        </span>,
      ].filter(Boolean)}
      actions={actions}
      wide
    >
      {open && <RunResult docId={run.docId} />}
    </DocumentCard>
  );
}

/** The result of a finished run, read from `GET /api/doc` only when the operator opens it. */
function RunResult({ docId }: { docId: string }) {
  const text = labels.testExtraction;
  const [view, setView] = useState<DocRecordView | 'missing' | 'error' | null>(null);

  // It mounts when the operator opens the result, so it reads once per opening.
  useEffect(() => {
    fetchDocRecord(docId)
      .then((record) => setView(record && !('deadLetter' in record) ? record : 'missing'))
      .catch(() => setView('error'));
  }, [docId]);

  const record = view && view !== 'missing' && view !== 'error' ? view : null;

  return (
    <div>
      {view === null && <p className="notice">{text.loading}</p>}
      {(view === 'error' || view === 'missing') && (
        <p className="notice notice--crit" role="alert">
          {view === 'missing' ? text.resultMissing : text.loadFailed}
        </p>
      )}
      {record && (
        <>
          <div className="action-row flow-md">
            <ResultJsonActions view={record} />
          </div>
          <GateReportBlock view={record} />
          <AuditTrail view={record} />
        </>
      )}
    </div>
  );
}
